import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import { Switch } from "@/components/shadcn/switch";
import { Textarea } from "@/components/shadcn/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Skeleton } from "@/components/shadcn/skeleton";
import { DeleteDialog } from "@/components/Modals/DeleteDialog";
import { SpotToast } from "@/components/SpotToast";
import { cn } from "@/lib/utils";
import {
  CreatePeriodicTaskPayload,
  PeriodicTask,
  PeriodicTasksService,
} from "@/services/periodicTasks";

const QUERY_KEY = "PERIODIC_TASKS";

const FREQUENCY_OPTIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
] as const;

const DAY_OPTIONS = [
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
  { value: "Sunday", label: "Sunday" },
] as const;

type Frequency = "hourly" | "daily" | "weekly" | "monthly";

function formatSchedule(task: PeriodicTask): string {
  switch (task.frequency) {
    case "hourly":
      return `Every hour${task.offset ? ` at :${String(task.offset).padStart(2, "0")}` : ""}`;
    case "daily":
      return `Daily at ${task.time_of_day || "00:00"} UTC`;
    case "weekly":
      return `${task.day_of_week || "Monday"} at ${task.time_of_day || "00:00"} UTC`;
    case "monthly":
      return `Day ${task.day_of_month || 1} at ${task.time_of_day || "00:00"} UTC`;
    default:
      return task.cron;
  }
}

interface TaskFormState {
  message: string;
  frequency: Frequency;
  time_of_day: string;
  day_of_week: string;
  day_of_month: string;
  offset: string;
  new_conversation: boolean;
  one_shot: boolean;
}

const DEFAULT_FORM: TaskFormState = {
  message: "",
  frequency: "daily",
  time_of_day: "09:00",
  day_of_week: "Monday",
  day_of_month: "1",
  offset: "0",
  new_conversation: true,
  one_shot: false,
};

function taskToForm(task: PeriodicTask): TaskFormState {
  return {
    message: task.message,
    frequency: task.frequency,
    time_of_day: task.time_of_day || "09:00",
    day_of_week: task.day_of_week || "Monday",
    day_of_month: String(task.day_of_month ?? 1),
    offset: String(task.offset ?? 0),
    new_conversation: task.new_conversation,
    one_shot: task.one_shot,
  };
}

function formToPayload(form: TaskFormState): CreatePeriodicTaskPayload {
  const payload: CreatePeriodicTaskPayload = {
    message: form.message,
    frequency: form.frequency,
    new_conversation: form.new_conversation,
    one_shot: form.one_shot,
  };
  if (form.frequency === "hourly") {
    payload.offset = parseInt(form.offset) || 0;
  }
  if (["daily", "weekly", "monthly"].includes(form.frequency)) {
    payload.time_of_day = form.time_of_day;
  }
  if (form.frequency === "weekly") {
    payload.day_of_week = form.day_of_week;
  }
  if (form.frequency === "monthly") {
    payload.day_of_month = parseInt(form.day_of_month) || 1;
  }
  return payload;
}

// -- TaskForm component --

function TaskForm({
  initialValues,
  onSave,
  onCancel,
  isSaving,
}: {
  initialValues: TaskFormState;
  onSave: (form: TaskFormState) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [form, setForm] = useState<TaskFormState>(initialValues);

  useEffect(() => {
    setForm(initialValues);
  }, [initialValues]);

  const update = <K extends keyof TaskFormState>(
    key: K,
    value: TaskFormState[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const isValid = form.message.trim().length > 0;

  return (
    <div className="flex flex-col gap-2 py-2">
      <div className="flex flex-col gap-1">
        <Label className="text-xs">Frequency</Label>
        <Select
          value={form.frequency}
          onValueChange={(v) => update("frequency", v as Frequency)}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {form.frequency === "hourly" && (
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Minute offset (0-59)</Label>
          <Input
            type="number"
            min={0}
            max={59}
            value={form.offset}
            onChange={(e) => update("offset", e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      )}

      {["daily", "weekly", "monthly"].includes(form.frequency) && (
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Time of day (UTC)</Label>
          <Input
            type="time"
            value={form.time_of_day}
            onChange={(e) => update("time_of_day", e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      )}

      {form.frequency === "weekly" && (
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Day of week</Label>
          <Select
            value={form.day_of_week}
            onValueChange={(v) => update("day_of_week", v)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {form.frequency === "monthly" && (
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Day of month (1-31)</Label>
          <Input
            type="number"
            min={1}
            max={31}
            value={form.day_of_month}
            onChange={(e) => update("day_of_month", e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Label className="text-xs">Message</Label>
        <Textarea
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="The complete instruction for the agent when this task fires..."
          className="text-xs min-h-[80px] resize-y"
        />
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">
          New conversation each time
        </Label>
        <Switch
          checked={form.new_conversation}
          onCheckedChange={(v) => update("new_conversation", v)}
          className="scale-75"
        />
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">
          One-shot (auto-delete after firing)
        </Label>
        <Switch
          checked={form.one_shot}
          onCheckedChange={(v) => update("one_shot", v)}
          className="scale-75"
        />
      </div>

      <div className="flex items-center gap-2 justify-end pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-7 text-xs"
          disabled={!isValid}
          loading={isSaving}
          onClick={() => onSave(form)}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

// -- Main component --

export function PeriodicTasksManager({
  groupId,
  assistantId,
}: {
  groupId: string;
  assistantId: string;
}) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState<"add" | string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PeriodicTask | null>(null);

  const queryKey = [QUERY_KEY, groupId, assistantId];

  const { data: tasks, isLoading } = useQuery({
    queryKey,
    queryFn: () => PeriodicTasksService.list(groupId, assistantId),
    enabled: Boolean(groupId && assistantId),
  });

  const taskList = useMemo(() => {
    if (!tasks?.data) return [];
    return tasks.data;
  }, [tasks]);

  const createMutation = useMutation({
    mutationFn: (payload: CreatePeriodicTaskPayload) =>
      PeriodicTasksService.create(groupId, assistantId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowForm(null);
      SpotToast.success("Scheduled task created");
    },
    onError: () => SpotToast.error("Failed to create scheduled task"),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      taskId,
      payload,
    }: {
      taskId: string;
      payload: CreatePeriodicTaskPayload;
    }) => PeriodicTasksService.update(groupId, assistantId, taskId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowForm(null);
      SpotToast.success("Scheduled task updated");
    },
    onError: () => SpotToast.error("Failed to update scheduled task"),
  });

  const deleteMutation = useMutation({
    mutationFn: (taskId: string) =>
      PeriodicTasksService.delete(groupId, assistantId, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setDeleteTarget(null);
      SpotToast.success("Scheduled task deleted");
    },
    onError: () => SpotToast.error("Failed to delete scheduled task"),
  });

  const handleSaveNew = (form: TaskFormState) => {
    createMutation.mutate(formToPayload(form));
  };

  const handleSaveEdit = (taskId: string) => (form: TaskFormState) => {
    updateMutation.mutate({ taskId, payload: formToPayload(form) });
  };

  return (
    <div className="border-t border-border/50 px-3 py-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          Scheduled tasks
        </span>
        {showForm !== "add" && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => setShowForm("add")}
          >
            <Plus className="h-3 w-3" />
            Add
          </Button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && taskList.length === 0 && showForm !== "add" && (
        <p className="text-xs text-muted-foreground text-center py-3">
          No scheduled tasks yet
        </p>
      )}

      {/* Task list */}
      {!isLoading &&
        taskList.map((task) => (
          <div key={task.id}>
            {showForm === task.id ? (
              <TaskForm
                initialValues={taskToForm(task)}
                onSave={handleSaveEdit(task.id)}
                onCancel={() => setShowForm(null)}
                isSaving={updateMutation.isPending}
              />
            ) : (
              <div
                className={cn(
                  "flex items-start gap-2 py-1.5 group",
                  "border-b border-border/30 last:border-b-0"
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 h-4"
                    >
                      {task.frequency}
                    </Badge>
                    {task.one_shot && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 h-4"
                      >
                        one-shot
                      </Badge>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      {formatSchedule(task)}
                    </span>
                  </div>
                  <p className="text-xs text-foreground/80 mt-0.5 line-clamp-2">
                    {task.message}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconsm"
                    onClick={() => setShowForm(task.id)}
                    title="Edit"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconsm"
                    onClick={() => setDeleteTarget(task)}
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}

      {/* Add form */}
      {showForm === "add" && (
        <TaskForm
          initialValues={DEFAULT_FORM}
          onSave={handleSaveNew}
          onCancel={() => setShowForm(null)}
          isSaving={createMutation.isPending}
        />
      )}

      {/* Delete dialog */}
      <DeleteDialog
        isOpen={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onDelete={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        title="Delete scheduled task?"
        text="This action cannot be undone. The task will stop firing immediately."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
